// =====================================================================
// Aba 6 — Agenda (Fase 3, S38).
// Edita campos do clinic_config relacionados a operacao da agenda:
//   working_hours_start (time), working_hours_end (time),
//   working_days (CSV de dias), avg_visit_duration_minutes,
//   clinic_timezone, clinic_email, clinic_address, clinic_neighborhood,
//   clinic_city, clinic_state.
//
// Usa o mesmo updateClinicConfig do Identidade Visual (PUT delta).
// =====================================================================
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Grid,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Divider
} from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import RefreshIcon from "@material-ui/icons/Refresh";
import { toast } from "react-toastify";

import WeekdaysPicker from "../components/WeekdaysPicker";
import {
  getClinicConfig,
  updateClinicConfig
} from "../../../services/clinicAdminService";

// Whitelist desta aba — disjunto das outras abas
const EDITABLE_FIELDS = [
  "working_hours_start",
  "working_hours_end",
  "working_days",
  "avg_visit_duration_minutes",
  "avg_time_at_clinic_text",
  "clinic_timezone",
  "clinic_email",
  "clinic_address",
  "clinic_neighborhood",
  "clinic_city",
  "clinic_state"
];

// Workflow valida formato Continent/City no PUT
const TZ_REGEX = /^[A-Za-z_]+\/[A-Za-z_]+$/;
// HH:MM ou HH:MM:SS (Postgres time)
const TIME_REGEX = /^\d{2}:\d{2}(:\d{2})?$/;

const useStyles = makeStyles((theme) => ({
  root: { width: "100%" },
  sectionTitle: {
    fontWeight: 700,
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginTop: theme.spacing(2.5),
    marginBottom: theme.spacing(1.25)
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    marginTop: theme.spacing(3)
  },
  loadingWrap: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(6)
  }
}));

// Trim HH:MM:SS pra HH:MM (input type=time precisa de HH:MM)
const trimTime = (t) => {
  if (!t) return "";
  const s = String(t);
  return s.length > 5 ? s.slice(0, 5) : s;
};

const AgendaTab = ({ slug }) => {
  const classes = useStyles();
  const [config, setConfig] = useState(null);
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!slug) return;
    try {
      setLoading(true);
      const data = await getClinicConfig(slug);
      const cfg = data && data.config ? data.config : data;
      // Normaliza times pra HH:MM (input type=time exige)
      if (cfg.working_hours_start) cfg.working_hours_start = trimTime(cfg.working_hours_start);
      if (cfg.working_hours_end)   cfg.working_hours_end   = trimTime(cfg.working_hours_end);
      setConfig(cfg || {});
      setOriginal(cfg || {});
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.message || "Erro carregando agenda");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const setField = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setConfig((prev) => ({ ...(prev || {}), [k]: v }));
  };

  const setRawField = (k, v) => {
    setConfig((prev) => ({ ...(prev || {}), [k]: v }));
  };

  const dirtyFields = useMemo(() => {
    if (!config || !original) return {};
    const out = {};
    EDITABLE_FIELDS.forEach((k) => {
      const before = original[k] ?? null;
      const after = config[k] ?? null;
      if (String(before ?? "") !== String(after ?? "")) {
        // Transforma avg_visit_duration_minutes pra inteiro antes de mandar
        if (k === "avg_visit_duration_minutes" && after !== "" && after != null) {
          out[k] = Number(after);
        } else {
          out[k] = after;
        }
      }
    });
    return out;
  }, [config, original]);

  const isDirty = Object.keys(dirtyFields).length > 0;

  const validate = () => {
    const c = config || {};
    if (c.working_hours_start && !TIME_REGEX.test(c.working_hours_start)) {
      toast.error("Horário inicial inválido (use HH:MM)"); return false;
    }
    if (c.working_hours_end && !TIME_REGEX.test(c.working_hours_end)) {
      toast.error("Horário final inválido (use HH:MM)"); return false;
    }
    if (c.clinic_timezone && !TZ_REGEX.test(c.clinic_timezone)) {
      toast.error("Fuso horário inválido (ex: America/Recife)"); return false;
    }
    if (c.avg_visit_duration_minutes != null && c.avg_visit_duration_minutes !== "") {
      const n = Number(c.avg_visit_duration_minutes);
      if (!Number.isInteger(n) || n < 0) {
        toast.error("Duração média deve ser inteiro >= 0"); return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (!isDirty) { toast.info("Nada pra salvar"); return; }
    if (!validate()) return;
    try {
      setSaving(true);
      await updateClinicConfig(slug, dirtyFields);
      toast.success("Configurações de agenda atualizadas");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.message || "Erro salvando");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => setConfig(original);

  if (loading) return <div className={classes.loadingWrap}><CircularProgress /></div>;
  if (!config) return <Typography color="error">Não foi possível carregar.</Typography>;

  const cfg = config;

  return (
    <div className={classes.root}>
      <Typography className={classes.sectionTitle}>Horário de funcionamento</Typography>

      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <TextField
            label="Abre às"
            variant="outlined"
            type="time"
            fullWidth
            size="small"
            value={cfg.working_hours_start || ""}
            onChange={setField("working_hours_start")}
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 300 }}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            label="Fecha às"
            variant="outlined"
            type="time"
            fullWidth
            size="small"
            value={cfg.working_hours_end || ""}
            onChange={setField("working_hours_end")}
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 300 }}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            label="Duração média (min)"
            variant="outlined"
            type="number"
            fullWidth
            size="small"
            value={cfg.avg_visit_duration_minutes ?? ""}
            onChange={setField("avg_visit_duration_minutes")}
            inputProps={{ min: 0 }}
            helperText="Slot padrão da agenda"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            label="Tempo médio em texto"
            variant="outlined"
            fullWidth
            size="small"
            value={cfg.avg_time_at_clinic_text || ""}
            onChange={setField("avg_time_at_clinic_text")}
            placeholder="1h30min na clínica"
          />
        </Grid>
      </Grid>

      <Typography className={classes.sectionTitle}>Dias úteis</Typography>
      <WeekdaysPicker
        value={cfg.working_days || ""}
        onChange={(v) => setRawField("working_days", v)}
      />

      <Typography className={classes.sectionTitle}>Fuso horário</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Timezone IANA"
            variant="outlined"
            fullWidth
            size="small"
            value={cfg.clinic_timezone || ""}
            onChange={setField("clinic_timezone")}
            placeholder="America/Recife"
            helperText="Formato Continent/City (ex: America/Recife, America/Sao_Paulo)"
            error={!!cfg.clinic_timezone && !TZ_REGEX.test(cfg.clinic_timezone)}
          />
        </Grid>
      </Grid>

      <Divider style={{ margin: "24px 0" }} />

      <Typography className={classes.sectionTitle}>Contato / Endereço</Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            label="E-mail"
            variant="outlined"
            type="email"
            fullWidth
            size="small"
            value={cfg.clinic_email || ""}
            onChange={setField("clinic_email")}
            placeholder="contato@clinicaclee.com"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Endereço"
            variant="outlined"
            fullWidth
            size="small"
            value={cfg.clinic_address || ""}
            onChange={setField("clinic_address")}
            placeholder="Rua Exemplo, 123"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Bairro"
            variant="outlined"
            fullWidth
            size="small"
            value={cfg.clinic_neighborhood || ""}
            onChange={setField("clinic_neighborhood")}
          />
        </Grid>
        <Grid item xs={8} md={5}>
          <TextField
            label="Cidade"
            variant="outlined"
            fullWidth
            size="small"
            value={cfg.clinic_city || ""}
            onChange={setField("clinic_city")}
          />
        </Grid>
        <Grid item xs={4} md={3}>
          <TextField
            label="UF"
            variant="outlined"
            fullWidth
            size="small"
            value={cfg.clinic_state || ""}
            onChange={setField("clinic_state")}
            inputProps={{ maxLength: 2 }}
          />
        </Grid>
      </Grid>

      <Box className={classes.actions}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleReset}
          disabled={!isDirty || saving}
        >
          Descartar
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={!isDirty || saving}
        >
          {saving ? "Salvando…" : `Salvar${isDirty ? ` (${Object.keys(dirtyFields).length})` : ""}`}
        </Button>
      </Box>
    </div>
  );
};

export default AgendaTab;
